import { Injectable } from '@angular/core';
import { CartOfferLine } from '~shared/modules/shopping-cart-store';
import { CartFeatures } from '~shared/modules/shopping-cart-store/models';
import { GATrackingEvent } from '../models';
import { newCheckoutGA4Events } from '~app/modules/checkout/constants';
import { RegularGtagEvent } from '../models/ga-tracking-ga-regular-event.model';
import { TrackingCategoryItem } from '~shared/modules/shopping-cart-global/models';
import { getCategoryIdHelper } from '~app/modules/generic/helpers/get-category-id.helper';
import { getCategoryNameHelper } from '~app/modules/generic/helpers/get-category-name.helper';
import { IOfferLine } from '~shared/modules/cart/models';
import { GATrackingEventType } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class GaTrackingCheckoutService {
  private static readonly CHECKOUT_LOCALSTORAGE_LAST_LOGIN_STATE = 'app-checkout/last-login-state';
  private static readonly CHECKOUT_SESSIONSTORAGE_ITEM_SELECTION_PAGES = 'app-checkout/items-selection-pages';

  static setItemSelectionPage(productId: string, pageName: string) {
    const selectionsJson = sessionStorage.getItem(this.CHECKOUT_SESSIONSTORAGE_ITEM_SELECTION_PAGES) ?? '{}';
    const currentSelections = this.parseJson(selectionsJson);
    currentSelections[productId] = pageName;
    const updatedSelectionsJson = JSON.stringify(currentSelections);
    sessionStorage.setItem(this.CHECKOUT_SESSIONSTORAGE_ITEM_SELECTION_PAGES, updatedSelectionsJson);
  }

  static getItemSelectionPage(productId: string) {
    const selectionsJson = sessionStorage.getItem(this.CHECKOUT_SESSIONSTORAGE_ITEM_SELECTION_PAGES) ?? '{}';
    let currentSelections = this.parseJson(selectionsJson);

    return currentSelections[productId];
  }

  static isCheckoutGA4Event(event: GATrackingEvent) {
    return newCheckoutGA4Events.some((gaEventType) => gaEventType === event.getEventType());
  }

  static convertGaToGA4GtagEvent(regularGtagEvent: RegularGtagEvent) {
    const items =
      regularGtagEvent.properties.gtmCustom?.items || regularGtagEvent.properties.gtmCustom?.ecommerce?.items || [];
    const coupon = regularGtagEvent.properties.gtmCustom?.coupon || undefined;
    const payment_method = regularGtagEvent?.properties?.gtmCustom?.payment_method ?? null;

    const eventPayload = {
      event: regularGtagEvent?.properties?.event,
      price_type: regularGtagEvent?.properties?.gtmCustom?.price_type,
      'content-name': regularGtagEvent?.properties?.gtmCustom['content-name'],
      authorization_flow: regularGtagEvent?.properties?.gtmCustom?.authorization_flow,
      ...this.getObjectKeyValue('authorization_type', regularGtagEvent?.properties?.gtmCustom),
      ...(payment_method ? { payment_method }: {}),
      ecommerce: {
        coupon,
        currency: regularGtagEvent?.properties?.gtmCustom?.currency,
        ...this.getObjectKeyValue('payment_method', regularGtagEvent?.properties?.gtmCustom),
        items: items.map((item: any) => {
          delete item.item_shipping_cost;
          item = this.mapCategoriesInfo(item);
          const noDimensionsItem = this.clearItemDimensions(item);

          return {
            ...noDimensionsItem,
            item_variant: undefined,
            item_list_name:
              GaTrackingCheckoutService.getItemSelectionPage(item.item_id) ||
              GaTrackingCheckoutService.getItemSelectionPage(item.id) ||
              item.item_list_name,
            item_black_deal_label: item.item_black_deal_label ?? item.dimension51,
            item_limited_stock: item.item_limited_stock ?? item.dimension52,
            item_offer_id: item.item_offer_id ?? item.dimension54,
            item_placeholder_attribute: item.item_placeholder_attribute ?? item.dimension43,
            item_on_sale: item.item_on_sale ?? item.dimension12,
          };
        }),
      },
    };
    if (regularGtagEvent.action === GATrackingEventType.ReviewOrderInfo){
      delete eventPayload.ecommerce;
    }
    return eventPayload;
  }

  private static clearItemDimensions(item: any) {
    const noDimensionsItem = { ...item };

    for (const key in noDimensionsItem) {
      if (Object.prototype.hasOwnProperty.call(item, key) && key.startsWith('dimension')) {
        delete noDimensionsItem[key];
      }
    }

    return noDimensionsItem;
  }

  static setCheckoutLastLoginFlag(type: string) {
    sessionStorage.setItem(this.CHECKOUT_LOCALSTORAGE_LAST_LOGIN_STATE, type.toLocaleLowerCase());
  }

  static removeCheckoutLastLogin(): void {
    sessionStorage.removeItem(this.CHECKOUT_LOCALSTORAGE_LAST_LOGIN_STATE);
  }

  static get getAuthorizeFlow(): Partial<{ authorization_flow: string; authorization_type: string }> {
    const authFlow = GaTrackingCheckoutService.checkoutLastLoginFlag;
    const authType = authFlow
      ? {
          authorization_type: authFlow,
        }
      : {};
    return {
      authorization_flow: authFlow === 'login' ? 'no' : 'yes',
      ...authType,
    };
  }

  static getItemsForApplicationEvent(
    cartOfferLines: CartOfferLine[],
    cartFeatures?: CartFeatures,
    previousUrl?: string,
  ): any[] {
    return cartOfferLines.map((cartOfferLine: IOfferLine) => {
      return {
        item_name: cartOfferLine?.item?.name,
        item_id: cartOfferLine?.item?.id,
        price: cartOfferLine?.price?.total,
        item_promo: cartOfferLine?.item?.hasPromotion ? 'yes' : 'no',
        item_volume_pricing: cartOfferLine?.trackingData?.volumePricingShown ? 'yes' : 'no',
        item_seller: cartOfferLine?.trackingData?.sellerName,
        item_offer_type: cartOfferLine?.trackingData?.offerType,
        item_strikethrough: cartOfferLine?.trackingData?.referencePrice?.priceType,
        item_list_name: cartOfferLine?.trackingData?.itemListName ?? previousUrl,
        item_discount_rate: cartOfferLine?.trackingData?.dimension31,
        item_service_addition: cartOfferLine?.trackingData?.dimension58,
        item_recommendation: cartOfferLine?.trackingData?.recommendation ? 'yes' : 'no',
        item_shipping_cost: cartOfferLine?.price?.shippingCostTotal,
        item_custom: GaTrackingCheckoutService.generateItemCustom(cartFeatures, cartOfferLine),
        quantity: cartOfferLine?.quantity,
        category_tree: cartOfferLine.trackingData?.categoryTree?.map((category: string | TrackingCategoryItem) => ({
          id: getCategoryIdHelper(category),
          name: getCategoryNameHelper(category),
        })),
        ...cartOfferLine?.trackingData?.categoryTree?.reduce(
          (categories: {}, category: string | TrackingCategoryItem, categoryIndex: number) => {
            let key = categoryIndex === 0 ? 'item_category' : `item_category${categoryIndex + 1}`;
            categories[key] = getCategoryNameHelper(category);
            return categories;
          },
          {},
        ),
        servicePrice: cartOfferLine.trackingData?.servicePrice,
        item_black_deal_label: cartOfferLine.trackingData?.dimension51,
        item_limited_stock: cartOfferLine.trackingData?.dimension52,
        item_offer_id: cartOfferLine.trackingData?.dimension54,
        item_placeholder_attribute: cartOfferLine.trackingData?.dimension43,
        item_on_sale: cartOfferLine.trackingData?.onSale,
        item_brand: cartOfferLine?.seller?.name,
      };
    });
  }

  private static get checkoutLastLoginFlag(): 'login' | 'registration' | null {
    return (sessionStorage.getItem(this.CHECKOUT_LOCALSTORAGE_LAST_LOGIN_STATE) as 'login' | 'registration') ?? null;
  }

  private static getObjectKeyValue(key: string, obj: any) {
    return obj && typeof obj === 'object' && obj.hasOwnProperty(key) ? { [key]: obj[key] } : {};
  }

  private static parseJson(json: string) {
    try {
      return JSON.parse(json);
    } catch (e) {
      return {};
    }
  }

  static mapCategoriesInfo(item: any) {
    let categories = item.category_tree?.map((category: { name: string }) => category.name) ?? [];
    categories = categories.slice(0, 6);

    for (let i = 0; i < categories.length; i++) {
      let categoryIndex = (i + 1).toString();

      if (i === 0) {
        categoryIndex = '';
      }

      delete item[`item_category_${categoryIndex}`];
      item[`item_category${categoryIndex}`] = categories[i];
    }

    return item;
  }

  static generateItemCustom(cartFeatures: CartFeatures, offerLine?: IOfferLine): string {
    let itemFeatures = [];
    const isBulky = offerLine?.deliveryOptionSelected;

    if (cartFeatures?.crossRegion) {
      itemFeatures.push(`cross_region=${cartFeatures.crossRegion}`);
    }
    if (cartFeatures?.isCrossBorder) {
      itemFeatures.push('xBorder');
    }
    if (isBulky) {
      itemFeatures.push(`delivery_option=${offerLine?.deliveryOptionSelected}`);
    }

    return itemFeatures.length ? itemFeatures.join('|') : null;
  }
}
